import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Image as ImageIcon,
  FileCode2,
  AlertCircle,
  Download,
  Eye,
  EyeOff,
  Copy,
  Check,
  Printer,
} from "lucide-react";
import { loadQRCode } from "@/lib/qrcode-lazy";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { FaqSection } from "@/components/seo/FaqSection";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { pageHead, breadcrumbJsonLd, softwareAppJsonLd, ldScript } from "@/lib/seo";
import { PrintSetupDialog } from "@/components/print/PrintSetupDialog";

const PATH = "/wifi-qr-code-generator";
const TITLE = "Wi-Fi QR Code Generator — Share Wi-Fi With One Scan";
const DESC =
  "Free Wi-Fi QR code generator. Enter your SSID, password and security type, then download a scannable QR as PNG or SVG — everything runs in your browser.";

const FAQ = [
  {
    q: "How Does a Wi-Fi QR Code Work?",
    a: "The QR encodes a standard string of the form WIFI:T:WPA;S:MySSID;P:mypassword;;. iOS, Android and most modern cameras recognise this format and offer a one-tap option to join the network — no typing required.",
  },
  {
    q: "Will It Work on iPhone and Android?",
    a: "Yes. iOS 11+ and Android 10+ scan Wi-Fi QR codes natively from the Camera app. Older Android devices may need a separate QR reader, but the payload format is the same.",
  },
  {
    q: "Is My Wi-Fi Password Sent Anywhere?",
    a: "No. The SSID and password are encoded into the QR code entirely in your browser using the open-source qrcode library. Nothing is uploaded, logged, or stored on our servers.",
  },
  {
    q: "What Security Types Are Supported?",
    a: "WPA / WPA2 / WPA3 (use WPA), the legacy WEP, and open networks (No password). For enterprise WPA2-Enterprise networks, Wi-Fi QR codes are not supported — guests still need to enter credentials manually.",
  },
  {
    q: "What Is a Hidden Network?",
    a: "A hidden SSID is one that does not broadcast its name. If your router is configured this way, enable the Hidden toggle so scanners know to prompt the device to search for it explicitly.",
  },
  {
    q: "Why Use Higher Error Correction?",
    a: "If you plan to print the QR small or stick it somewhere it might get scuffed (a café wall, a rental fridge), use Q or H so it still scans when partially damaged.",
  },
  {
    q: "Are Wi-Fi QR Code Generators Safe?",
    a: "This one is, because the code is built in your browser and the password is never sent to a server. Keep in mind that anyone who scans the printed code can read the password from it, so share codes only for a guest network. As a guest, be wary of a Wi-Fi sticker that looks pasted over another, since it could point you to a network run by someone else.",
  },
  {
    q: "Can I Make a Wi-Fi QR Code on iPhone?",
    a: "Yes. Open this page in Safari on the iPhone, fill in the network name and password, and tap PNG; Safari saves the image to the Downloads folder in the Files app. Scan it with a second phone before printing to confirm it joins.",
  },
  {
    q: "Does a Wi-Fi QR Code Work Without a Password?",
    a: "Yes. Set Security to No password (open network) and the code holds only the network name, marked as an open network. A phone that scans it joins straight away, and any sign-in page the network uses appears after joining as usual.",
  },
];

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Generators", path: "/generators" },
  { name: "Wi-Fi QR Code Generator", path: PATH },
];

type EcLevel = "L" | "M" | "Q" | "H";
type Security = "WPA" | "WEP" | "nopass";

const HEX_COLOR = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** Escape per the WIFI: URI spec — backslash, semicolon, comma, colon and quote. */
function escapeWifi(value: string): string {
  return value.replace(/([\\;,:"])/g, "\\$1");
}

function buildWifiPayload(input: {
  ssid: string;
  password: string;
  security: Security;
  hidden: boolean;
}): string {
  const t = input.security;
  const s = escapeWifi(input.ssid);
  const p = t === "nopass" ? "" : escapeWifi(input.password);
  const h = input.hidden ? "true" : "false";
  // Order recommended by the Wi-Fi Alliance / Android: T;S;P;H;
  return `WIFI:T:${t};S:${s};${t === "nopass" ? "" : `P:${p};`}H:${h};;`;
}

function validateInputs(input: {
  ssid: string;
  password: string;
  security: Security;
  darkColor: string;
  lightColor: string;
}): string | null {
  const { ssid, password, security, darkColor, lightColor } = input;
  if (ssid.length === 0) return "Enter the Wi-Fi network name (SSID).";
  if (ssid.length > 32)
    return `SSID is too long (${ssid.length} chars). Wi-Fi SSIDs are limited to 32 characters.`;
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f\x7f]/.test(ssid))
    return "SSID contains control characters. Remove them and try again.";

  if (security !== "nopass") {
    if (password.length === 0) return "Enter the Wi-Fi password, or set Security to “No password”.";
    if (security === "WPA") {
      if (password.length < 8 || password.length > 63) {
        return `WPA passwords must be 8–63 characters (got ${password.length}).`;
      }
    } else if (security === "WEP") {
      const isHex = /^[0-9a-fA-F]+$/.test(password);
      const okAscii = [5, 13, 16, 29].includes(password.length);
      const okHex = isHex && [10, 26, 32, 58].includes(password.length);
      if (!okAscii && !okHex) {
        return "WEP keys must be 5/13/16/29 ASCII characters or 10/26/32/58 hex characters.";
      }
    }
  }

  if (!HEX_COLOR.test(darkColor))
    return "Foreground colour must be a hex value like #0f172a or #000.";
  if (!HEX_COLOR.test(lightColor))
    return "Background colour must be a hex value like #ffffff or #fff.";
  if (darkColor.toLowerCase() === lightColor.toLowerCase())
    return "Foreground and background colours must differ.";
  return null;
}

function safeFilename(ssid: string): string {
  const slug = ssid.replace(/[^a-zA-Z0-9_-]+/g, "_").slice(0, 40) || "network";
  return `wifi-${slug}`;
}

function WifiQrCodeGeneratorPage() {
  const [ssid, setSsid] = useState("MyNetwork");
  const [password, setPassword] = useState("supersecret");
  const [security, setSecurity] = useState<Security>("WPA");
  const [hidden, setHidden] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [ecLevel, setEcLevel] = useState<EcLevel>("M");
  const [size, setSize] = useState(320);
  const [margin, setMargin] = useState(2);
  const [darkColor, setDarkColor] = useState("#0f172a");
  const [lightColor, setLightColor] = useState("#ffffff");

  const [error, setError] = useState<string | null>(null);
  const [svgMarkup, setSvgMarkup] = useState("");
  const [copied, setCopied] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const payload = useMemo(
    () => buildWifiPayload({ ssid, password, security, hidden }),
    [ssid, password, security, hidden],
  );

  const opts = useMemo(
    () => ({
      errorCorrectionLevel: ecLevel,
      margin,
      width: size,
      color: { dark: darkColor, light: lightColor },
    }),
    [ecLevel, margin, size, darkColor, lightColor],
  );

  useEffect(() => {
    const validationErr = validateInputs({ ssid, password, security, darkColor, lightColor });
    if (validationErr) {
      setError(validationErr);
      setSvgMarkup("");
      const c = canvasRef.current;
      if (c) c.getContext("2d")?.clearRect(0, 0, c.width, c.height);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const QRCode = await loadQRCode();
        if (canvasRef.current) {
          await QRCode.toCanvas(canvasRef.current, payload, opts);
        }
        const svg = await QRCode.toString(payload, { ...opts, type: "svg" });
        if (cancelled) return;
        setSvgMarkup(svg);
        setError(null);
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : "Failed to render Wi-Fi QR code.";
        setError(
          msg.toLowerCase().includes("too big")
            ? "Payload is too long for the selected error correction. Shorten the SSID/password or lower the error correction level."
            : msg,
        );
        setSvgMarkup("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [payload, opts, ssid, password, security, darkColor, lightColor]);

  const downloadPng = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeFilename(ssid)}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  };

  const downloadSvg = () => {
    if (!svgMarkup) return;
    const blob = new Blob([svgMarkup], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeFilename(ssid)}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyPayload = async () => {
    try {
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="Wi-Fi QR Code Generator"
      description="Turn your Wi-Fi network into a scannable QR code. Guests join with one tap from the iPhone or Android camera — no typing the password. Enter the network name, password and security type (WPA, WEP or none), then download a PNG or SVG or print a Wi-Fi card."
      crumbs={CRUMBS}
      content={
        <>
          <HowToScanSection />

          <ToolSeoSections
            aboutHeading="About the Wi-Fi QR Code Generator"
            about={
              <>
                <p>
                  The ReadBarcode Wi-Fi QR code generator turns your network name, password and security
                  type into a scannable QR code that joins the Wi-Fi in one tap. Print it, stick it on
                  the fridge, or drop it into a welcome guide — no more reading a 20-character WPA
                  password out loud.
                </p>
                <p>
                  The encoded string follows the de-facto <code>WIFI:T:…;S:…;P:…;H:…;;</code> format
                  that iOS Camera, Android Camera and most third-party QR readers recognise natively.
                  Generation happens entirely in your browser using the open-source qrcode library —
                  your password never leaves your device.
                </p>
              </>
            }
            whenToUse={[
              "Cafés, restaurants and co-working spaces sharing guest Wi-Fi.",
              "Airbnbs and short-term rentals — print one card for the kitchen.",
              "Conference rooms and event venues with rotating guest networks.",
              "Home networks when family or visitors keep asking for the password.",
              "Schools, classrooms and libraries with shared device carts.",
              "Retail backrooms where staff devices need to rejoin the network.",
            ]}
            howItWorks={[
              {
                title: "Enter your network",
                desc: "Type the SSID exactly as it appears in your router settings.",
              },
              {
                title: "Pick security",
                desc: "WPA covers WPA/WPA2/WPA3. Use WEP only for legacy networks, or No password for open Wi-Fi.",
              },
              {
                title: "Enter the password",
                desc: "8–63 characters for WPA. Toggle the eye icon to verify what you typed.",
              },
              {
                title: "Flag hidden networks",
                desc: "Enable Hidden if your router does not broadcast its SSID.",
              },
              {
                title: "Download PNG or SVG",
                desc: "PNG for screens and quick prints. SVG for cards, signs and packaging.",
              },
            ]}
            whatToAvoid={[
              "Typos in the SSID — Wi-Fi names are case sensitive and a single wrong character means no connection.",
              "Sharing a QR for your main network with strangers — set up a separate guest SSID instead.",
              "Printing the QR very small without bumping error correction to Q or H.",
              "Inverting the colours (light QR on a dark background) — many phone cameras refuse to scan them.",
              "Embedding control characters or emoji that some routers strip — keep the SSID printable ASCII.",
            ]}
            tips={[
              "Print the QR alongside the SSID and password in text — useful for devices that cannot scan.",
              "Laminate it if it lives somewhere wet (kitchen, gym, pool).",
              "Rotate the guest password regularly and regenerate the QR — it takes 10 seconds.",
              "Test the QR with both an iPhone and an Android before you print a batch.",
              "Use the QR Code Reader on this site to confirm the payload decodes correctly.",
            ]}
          />
          <PrivacyNote />
          <h2>How to Make a Wi-Fi QR Code for Guests</h2>
          <p>Enter your guest network's name and password in the generator above, then print the code where visitors can scan it.</p>
          <ol>
            <li>Turn on a guest network in your router and note its name and password.</li>
            <li>Type the name into Network name (SSID) and pick the security type the router uses.</li>
            <li>Enter the password, and raise error correction to Q or H if the code will be printed small.</li>
            <li>Scan the preview with a phone to confirm it joins, then download or print it.</li>
          </ol>
          <p>This free Wi-Fi QR code generator has no username field, so it cannot cover WPA2-Enterprise networks that need a username and password.</p>
          <h3>Connect to Wi-Fi With a QR Code</h3>
          <p>Guests connect to Wi-Fi with the QR code in one scan: the phone reads the network name, security type and password and offers to join. Nothing is typed, so a long random password is as easy to share as a short one.</p>
          <p>If joining fails, the code probably no longer matches the router's password or security type, so generate a new one. The <Link to="/qr-code-reader">QR code reader</Link> recognizes Wi-Fi codes and lists their fields, which shows what a printed code contains.</p>
          <h2>Generate a Wi-Fi QR Code Offline and Privately</h2>
          <p>This online Wi-Fi QR code generator builds the code in your browser, so once the page has loaded it needs no server. You can disconnect from the internet before typing the password and the preview still updates.</p>
          <p>The Wi-Fi payload line under the preview shows the exact string inside the code, so you can confirm it holds only the details you entered.</p>
          <h2>Print a Wi-Fi QR Code Sign With Text</h2>
          <p>Click Print and choose the Wi-Fi card layout to print the code with the network name, password and security type beside it in plain text. Guests whose device cannot scan can type the details instead.</p>
          <p>The card is credit-card sized and prints one per page or ten per sheet on Avery L7414 (A4) or Avery 5371 (US Letter) business-card stock. For a larger sign, choose Single label, set the width and edit the caption under the code. To encode a link or plain text instead, use the <Link to="/qr-code-generator">QR code generator</Link>.</p>
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <div className="mt-6 grid gap-6 md:grid-cols-2 [&>*]:min-w-0">
        <Card>
          <CardContent className="space-y-5 p-6">
            <div className="space-y-2">
              <Label htmlFor="ssid">Network name (SSID)</Label>
              <Input
                id="ssid"
                value={ssid}
                onChange={(e) => setSsid(e.target.value)}
                placeholder="MyHomeNetwork"
                maxLength={32}
                spellCheck={false}
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                Exactly as it appears in your router — case sensitive, 1–32 characters.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="security">Security</Label>
              <Select value={security} onValueChange={(v) => setSecurity(v as Security)}>
                <SelectTrigger id="security">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="WPA">WPA / WPA2 / WPA3 (most common)</SelectItem>
                  <SelectItem value="WEP">WEP (legacy)</SelectItem>
                  <SelectItem value="nopass">No password (open network)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {security !== "nopass" && (
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your Wi-Fi password"
                    maxLength={63}
                    spellCheck={false}
                    autoComplete="off"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {security === "WPA"
                    ? "WPA passwords are 8–63 characters."
                    : "WEP keys: 5/13/16/29 ASCII or 10/26/32/58 hex."}
                </p>
              </div>
            )}

            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <Label htmlFor="hidden" className="cursor-pointer">
                  Hidden network
                </Label>
                <p className="text-xs text-muted-foreground">
                  Enable if your router does not broadcast its SSID.
                </p>
              </div>
              <Switch id="hidden" checked={hidden} onCheckedChange={setHidden} />
            </div>

            <details className="rounded-md border border-border">
              <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium">
                QR appearance
              </summary>
              <div className="space-y-4 border-t border-border p-4">
                <div className="space-y-2">
                  <Label htmlFor="ec">Error correction</Label>
                  <Select value={ecLevel} onValueChange={(v) => setEcLevel(v as EcLevel)}>
                    <SelectTrigger id="ec">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="L">L — ~7% recovery (smallest)</SelectItem>
                      <SelectItem value="M">M — ~15% recovery (default)</SelectItem>
                      <SelectItem value="Q">Q — ~25% recovery</SelectItem>
                      <SelectItem value="H">H — ~30% recovery (logo overlays)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Size</Label>
                    <span className="text-xs text-muted-foreground">{size}px</span>
                  </div>
                  <Slider
                    min={128}
                    max={1024}
                    step={16}
                    value={[size]}
                    onValueChange={(v) => setSize(v[0])}
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Quiet zone</Label>
                    <span className="text-xs text-muted-foreground">{margin} modules</span>
                  </div>
                  <Slider
                    min={0}
                    max={8}
                    step={1}
                    value={[margin]}
                    onValueChange={(v) => setMargin(v[0])}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3 [&>*]:min-w-0">
                  <div className="space-y-2">
                    <Label htmlFor="dark">Foreground</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="dark"
                        type="color"
                        value={darkColor}
                        onChange={(e) => setDarkColor(e.target.value)}
                        className="h-9 w-12 cursor-pointer p-1"
                      />
                      <Input
                        value={darkColor}
                        onChange={(e) => setDarkColor(e.target.value)}
                        className="min-w-0 font-mono text-xs"
                        aria-label="Foreground hex"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="light">Background</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="light"
                        type="color"
                        value={lightColor}
                        onChange={(e) => setLightColor(e.target.value)}
                        className="h-9 w-12 cursor-pointer p-1"
                      />
                      <Input
                        value={lightColor}
                        onChange={(e) => setLightColor(e.target.value)}
                        className="min-w-0 font-mono text-xs"
                        aria-label="Background hex"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </details>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex h-full flex-col gap-4 p-6">
            <div
              className="flex min-h-[280px] flex-1 items-center justify-center rounded-md border border-dashed border-border p-4"
              style={{ backgroundColor: error ? undefined : lightColor }}
            >
              {error ? (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" aria-hidden />
                  <span>{error}</span>
                </div>
              ) : (
                <canvas
                  ref={canvasRef}
                  className="max-h-[420px] max-w-full"
                  aria-label="Generated Wi-Fi QR code preview"
                />
              )}
            </div>

            <div className="rounded-md border border-border bg-muted/30 p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-medium text-muted-foreground">Wi-Fi payload</div>
                  <code className="mt-1 block truncate text-xs">{payload}</code>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={copyPayload}
                  disabled={!!error}
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Button onClick={downloadPng} disabled={!!error}>
                <ImageIcon className="h-4 w-4" aria-hidden />
                PNG
              </Button>
              <Button variant="outline" onClick={downloadSvg} disabled={!!error || !svgMarkup}>
                <FileCode2 className="h-4 w-4" aria-hidden />
                SVG
              </Button>
              <Button
                variant="outline"
                onClick={() => setPrintOpen(true)}
                disabled={!!error || !svgMarkup}
              >
                <Printer className="h-4 w-4" aria-hidden />
                Print
              </Button>
            </div>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Download className="h-3.5 w-3.5" aria-hidden />
              Files are generated on your device — nothing is uploaded.
            </p>
          </CardContent>
        </Card>
      </div>

      <PrintSetupDialog
        open={printOpen}
        onOpenChange={setPrintOpen}
        title={`Wi-Fi QR · ${ssid || "network"}`}
        filename={safeFilename(ssid)}
        svgMarkup={svgMarkup}
        defaultCaption={ssid ? `Wi-Fi: ${ssid}` : ""}
        defaults={{
          layoutKind: "card",
          widthMm: 50,
          sheetPreset: "averyL7160",
          paper: "A4",
          orientation: "portrait",
        }}
        cardTemplate={{
          kind: "wifi-card",
          optionLabel: "Wi-Fi card",
          data: { ssid, password, security, hidden },
        }}
      />
    </ToolLayout>
  );
}

function HowToScanSection() {
  return (
    <ul>
      {[
        {
          title: "iPhone",
          body: "Open the Camera app, point at the QR, and tap the Wi-Fi notification. Works on iOS 11 and newer.",
        },
        {
          title: "Android",
          body: "Open the Camera (or Google Lens) on Android 10+, point at the QR, and tap the Connect prompt.",
        },
        {
          title: "Other devices",
          body: "Any QR reader that supports the WIFI: URI scheme will offer a one-tap join — common in modern Samsung, Xiaomi and Pixel cameras.",
        },
      ].map((s) => (
        <li key={s.title}>
          <strong>{s.title}</strong>
          {` ${s.body}`}
        </li>
      ))}
    </ul>
  );
}

function PrivacyNote() {
  return (
    <>
      <h2>Your Password Stays on Your Device</h2>
      <p>
        SSIDs and passwords are encoded locally with the open-source qrcode library. Nothing is
        uploaded, logged, or saved to our servers — close the tab and the credentials are gone.
      </p>
    </>
  );
}

function RelatedTools() {
  const related = [
    {
      path: "/qr-code-generator",
      label: "QR Code Generator",
      desc: "Encode any URL, text or vCard with custom colours and error correction.",
    },
    {
      path: "/qr-code-reader",
      label: "QR Code Reader",
      desc: "Scan the QR you just generated to verify the Wi-Fi payload decodes.",
    },
    {
      path: "/barcode-generator",
      label: "Barcode Generator",
      desc: "Generate UPC, EAN, Code 128 and more as PNG or SVG.",
    },
    {
      path: "/barcode-guides/1d-vs-2d-barcodes",
      label: "1D vs 2D barcodes",
      desc: "Why QR is the right choice for Wi-Fi sharing, not a 1D barcode.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-lg font-semibold">Related Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {related.map((r) => (
          <a
            key={r.path}
            href={r.path}
            className="group rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/40"
          >
            <div className="text-sm font-medium group-hover:text-primary">{r.label}</div>
            <div className="text-xs text-muted-foreground">{r.desc}</div>
          </a>
        ))}
      </div>
    </section>
  );
}

export const Route = createFileRoute("/wifi-qr-code-generator")({
  component: WifiQrCodeGeneratorPage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
    });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode Wi-Fi QR Code Generator",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});
